import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { db, LocalHabit } from '../../utils/db';
import { generateUUID } from '../../utils/security';

export type Habit = LocalHabit;

interface HabitsState {
  habits: Habit[];
  isLoading: boolean;
  error: string | null;
}

const initialState: HabitsState = {
  habits: [],
  isLoading: false,
  error: null,
};

// Async thunks - 全て IndexedDB (db.ts) を直接操作するように修正
export const fetchHabits = createAsyncThunk(
  'habits/fetchHabits',
  async (includeArchived: boolean = false) => {
    let habits: Habit[];
    if (includeArchived) {
      habits = await db.habits.orderBy('displayOrder').toArray();
    } else {
      habits = await db.habits
        .where('isArchived')
        .equals(0)
        .sortBy('displayOrder');
    }
    return habits;
  }
);

export const createHabit = createAsyncThunk(
  'habits/createHabit',
  async (habitData: Partial<Habit>) => {
    if (!habitData.name) throw new Error('Habit name is required');

    const newHabit: LocalHabit = {
      uuid: generateUUID(),
      name: habitData.name,
      description: habitData.description || '',
      color: habitData.color || '#4F46E5',
      icon: habitData.icon || '',
      repeatDays: habitData.repeatDays || {
        mon: true, tue: true, wed: true, thu: true,
        fri: true, sat: true, sun: true
      },
      goalType: habitData.goalType || 'continuous',
      weeklyGoal: habitData.goalType === 'weekly' ? (habitData.weeklyGoal || 3) : undefined,
      displayOrder: (await db.habits.count()) + 1,
      isArchived: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalCompleted: 0,
      reminderEnabled: habitData.reminderEnabled || 0,
      reminderTime: habitData.reminderTime || '09:00',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const id = await db.habits.add(newHabit);
    return { ...newHabit, id: id as number } as Habit;
  }
);

export const updateHabit = createAsyncThunk(
  'habits/updateHabit',
  async (payload: { id: number; data: Partial<Habit> }) => {
    const { id, data } = payload;
    if (!id) throw new Error('Habit id is required');

    return await db.transaction('rw', db.habits, async () => {
      const existingHabit = await db.habits.get(id);
      if (!existingHabit) throw new Error('Habit not found');

      const updatedHabit: Habit = {
        ...existingHabit,
        ...data,
        updatedAt: new Date(),
      };

      await db.habits.put(updatedHabit);
      return updatedHabit;
    });
  }
);

export const reorderHabitsInDB = createAsyncThunk(
  'habits/reorderHabitsInDB',
  async (orderedHabits: Habit[]) => {
    await db.transaction('rw', db.habits, async () => {
      for (let i = 0; i < orderedHabits.length; i++) {
        const habit = orderedHabits[i];
        if (habit.id) {
          await db.habits.update(habit.id, { displayOrder: i });
        }
      }
    });
    return orderedHabits;
  }
);

export const deleteHabit = createAsyncThunk(
  'habits/deleteHabit',
  async (id: number) => {
    await db.transaction('rw', [db.habits, db.records], async () => {
      const habit = await db.habits.get(id);
      if (habit) {
        await db.records.where('habitUuid').equals(habit.uuid).delete();
        await db.habits.delete(id);
      }
    });
    return id;
  }
);

export const archiveHabitAction = createAsyncThunk(
  'habits/archiveHabit',
  async (id: number) => {
    return await db.transaction('rw', db.habits, async () => {
      const habit = await db.habits.get(id);
      if (!habit) throw new Error('Habit not found');

      const updatedHabit: Habit = {
        ...habit,
        isArchived: 1,
        updatedAt: new Date(),
      };

      await db.habits.put(updatedHabit);
      return updatedHabit;
    });
  }
);

const habitsSlice = createSlice({
  name: 'habits',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // UI側の即時反映用（実際の保存は reorderHabitsInDB で行う）
    updateHabitOrderLocal: (state, action: PayloadAction<Habit[]>) => {
      state.habits = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHabits.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchHabits.fulfilled, (state, action) => {
      state.isLoading = false;
      state.habits = action.payload;
    });
    builder.addCase(fetchHabits.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch habits';
    });

    builder.addCase(createHabit.fulfilled, (state, action) => {
      state.habits.push(action.payload);
    });

    builder.addCase(updateHabit.fulfilled, (state, action) => {
      const index = state.habits.findIndex(h => h.id === action.payload.id);
      if (index !== -1) {
        state.habits[index] = action.payload;
      }
    });

    builder.addCase(reorderHabitsInDB.fulfilled, (state, action) => {
      state.habits = action.payload;
    });

    builder.addCase(deleteHabit.fulfilled, (state, action) => {
      state.habits = state.habits.filter(h => h.id !== action.payload);
    });

    builder.addCase(archiveHabitAction.fulfilled, (state, action) => {
      state.habits = state.habits.filter(h => h.id !== action.payload.id);
    });
  },
});

export const { clearError, updateHabitOrderLocal } = habitsSlice.actions;
export default habitsSlice.reducer;
